/**
 * Menu scroll indicator
 *
 * Sets data-scroll-state="more" on any [data-scroll-indicator-target] when:
 *   - content overflows the container (scrollHeight > clientHeight), AND
 *   - user has not yet scrolled to the bottom.
 *
 * Removes the attribute otherwise. CSS keys all visual states off this.
 */
(function () {
  const TARGET_SELECTOR = '[data-scroll-indicator-target]';
  // 2px tolerance absorbs sub-pixel rounding between scrollTop (integer in
  // most browsers) and scrollHeight/clientHeight (can be fractional). 1px
  // was too tight — scroll-to-bottom in the Letters drawer could leave
  // ~1.5px slop that failed the atBottom check, leaving the fade stuck on.
  const EPSILON = 2;

  function updateState(el) {
    const overflows = el.scrollHeight > el.clientHeight + EPSILON;
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - EPSILON;
    if (overflows && !atBottom) {
      el.setAttribute('data-scroll-state', 'more');
    } else {
      el.removeAttribute('data-scroll-state');
    }
  }

  function applyVariantClass(el) {
    const marker = el.querySelector('[data-menu-scroll-variant]');
    if (marker) {
      el.setAttribute(
        'data-menu-scroll-variant',
        marker.dataset.menuScrollVariant
      );
    }
  }

  function observe(el) {
    if (el.__menuScrollIndicatorObserved) return;
    el.__menuScrollIndicatorObserved = true;

    applyVariantClass(el);

    el.addEventListener('scroll', () => updateState(el), { passive: true });

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => updateState(el));
      ro.observe(el);
      // Also observe each child so we catch height growth from inside the
      // scroll container — e.g. lazy-loaded images finishing, font swap,
      // CSS-driven reflow at breakpoints. RO on `el` alone only fires for
      // `el`'s own box, missing descendant growth that changes scrollHeight.
      Array.from(el.children).forEach((child) => ro.observe(child));
    }

    if ('MutationObserver' in window) {
      const mo = new MutationObserver(() => updateState(el));
      mo.observe(el, { childList: true, subtree: true });
    }

    requestAnimationFrame(() => updateState(el));
  }

  function init() {
    document.querySelectorAll(TARGET_SELECTOR).forEach(observe);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  // Window resize doesn't always change the scroll container's own box
  // (e.g. width-only resize above 600px keeps the drawer body's box stable),
  // so the per-element ResizeObserver alone can miss viewport-driven reflows.
  // Re-run updateState for every target on resize.
  window.addEventListener(
    'resize',
    () => {
      document
        .querySelectorAll(TARGET_SELECTOR)
        .forEach((el) => updateState(el));
    },
    { passive: true }
  );
})();
